import {
  ChevronRight,
  FileText,
  UploadCloud,
  User,
  Eye,
  EyeOff,
  IndianRupee,
  Landmark,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import TopNavbar from "../../components/AdminPortal/TopNavbar";
import { useSidebar } from "../../context/SidebarContext";
import { toast } from "react-toastify";
import { affiliateAPI } from "../../services/AdminPortal/affiliateAPI";
import { BsCalendarDate } from "react-icons/bs";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
export default function AffiliatePartnerForm() {
  const { sidebarExpanded, isMobile } = useSidebar();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState({
    idProof: null,
    addressProof: null,
    bankStatement: null,
    agreement: null,
    canceledCheck: null,
    profilePhoto: null,
  });
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    emailId: "",
    password: "",
    dateOfJoining: "",
    affiliateCategory: "",
    accountHolderName: "",
    accountNumber: "",
    bankName: "",
    bankBranch: "",
    ifscCode: "",
    commissionRate: "",
    fixedCommission: "",
    payoutCycle: "monthly",
    paymentThreshold: "",
    location: "",
    pinCode: "",
    age: "",
    linkedIn: "",
    instagram: "",
    facebook: "",
    whatsappNumber: "",
    commissionMin: "",
    commissionMax: "",
    // ✅ Branch fields
    branchName: "",
    branchType: "",
    branchLocation: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      // Reduce max file size to 2MB to prevent 413 errors
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      if (file.size > maxSize) {
        toast.error("File too large. Maximum size is 2MB");
        return;
      }

      // Validate file type
      const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Invalid file type. Please upload PDF, JPEG, or PNG files");
        return;
      }

      console.log(`File selected for ${type}:`, file);
      setFiles((prev) => ({
        ...prev,
        [type]: file,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      console.log("Starting affiliate creation process...");

      // Prepare the affiliate data in the expected format
      const affiliateData = {
        name: formData.name,
        phoneNumber: formData.phoneNumber,
        emailId: formData.emailId,
        password: formData.password,
        dateOfJoining: formData.dateOfJoining
          ? new Date(formData.dateOfJoining).toISOString()
          : "",
        affiliateCategory: formData.affiliateCategory,
        accountHolderName: formData.accountHolderName,
        accountNumber: formData.accountNumber,
        bankName: formData.bankName,
        bankBranch: formData.bankBranch,
        ifscCode: formData.ifscCode,
        commissionRate: parseFloat(formData.commissionRate),
        fixedCommission: parseFloat(formData.fixedCommission),
        payoutCycle: formData.payoutCycle,
        paymentThreshold: parseFloat(formData.paymentThreshold),
        location: formData.location,
        pinCode: formData.pinCode,
        age: formData.age,
        linkedIn: formData.linkedIn,
        instagram: formData.instagram,
        facebook: formData.facebook,
        whatsappNumber: formData.whatsappNumber,
        commissionMin: formData.commissionMin,
        commissionMax: formData.commissionMax,
        // ✅ Branch fields
        branchName: formData.branchName,
        branchType: formData.branchType,
        branchLocation: formData.branchLocation,
        documents: [],
      };

      console.log("Formatted affiliate data:", affiliateData);

      // Create affiliate partner record
      const response = await affiliateAPI.create(affiliateData);
      console.log("Create affiliate response:", response);

      if (response.success) {
        const affiliateId = response.data._id;
        console.log("Created affiliate with ID:", affiliateId);

        // Check if required documents are present
        const requiredFiles = ['idProof', 'addressProof', 'bankStatement', 'agreement', 'canceledCheck', 'profilePhoto'];
        const missingFiles = requiredFiles.filter(fileType => !files[fileType]);
        
        if (missingFiles.length > 0) {
          toast.error(`Missing required documents: ${missingFiles.join(', ')}`);
          // Delete the created affiliate since documents are missing
          await affiliateAPI.delete(affiliateId);
          setIsLoading(false);
          return;
        }

        // Upload documents sequentially
        try {
          console.log("Starting document uploads...");
          
          const uploadFile = async (file, type) => {
            const uploadResponse = await affiliateAPI.uploadDocument(file, type, affiliateId);
            if (!uploadResponse.success) {
              throw new Error(`Failed to upload ${type}`);
            }
            return uploadResponse;
          };

          // Upload all files sequentially
          await uploadFile(files.idProof, "id_proof");
          await uploadFile(files.addressProof, "address_proof");
          await uploadFile(files.bankStatement, "bank_statement");
          await uploadFile(files.agreement, "agreement");
          await uploadFile(files.canceledCheck, "canceled_check");
          await uploadFile(files.profilePhoto, "profile_photo");
          
          console.log("All documents uploaded successfully");
          toast.success("Affiliate partner created successfully!");
          navigate("/affiliate-partner/dashboard");
        } catch (uploadError) {
          console.error("Error uploading documents:", uploadError);
          toast.error("Document upload failed. Partner creation cancelled.");
          // Delete the created affiliate since document upload failed
          await affiliateAPI.delete(affiliateId);
          setIsLoading(false);
          return;
        }
      } else {
        console.error("Failed to create affiliate:", response);
        toast.error(response.message || "Failed to create affiliate partner");
      }
    } catch (error) {
      console.error("Error creating affiliate partner:", error);
      toast.error(error.message || "Failed to create affiliate partner");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/affiliate-partners");
  };

  return (
    <div
      className="min-h-screen bg-[#000000] text-white mt-12"
      style={{
        marginLeft: isMobile ? "0" : sidebarExpanded ? "200px" : "60px",
        width: isMobile
          ? "100%"
          : `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
      }}
    >
      <TopNavbar />

      {/* Header */}
      <div className="bg-[#000000] p-4 border-b border-gray-800">
        <h1 className="text-gray-400 text-lg">
          Affiliate Partner - New Partner
        </h1>
      </div>

      {/* Main Content */}
      <div className="p-4 bg-[#000000]">
        <div className="flex items-center mb-6">
          <a
            href="#"
            className="text-[#FFCA20] hover:underline"
            onClick={(e) => {
              e.preventDefault();
              navigate("/affiliate-partner/dashboard");
            }}
          >
            Affiliate Partner Management
          </a>
          <span className="mx-2 text-gray-500">{">"}</span>
          <span>New Affiliate Partner</span>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Affiliate Partner Details */}
          <div className="bg-[#131313] rounded-lg p-4 sm:p-6 mb-6">
            <div className="flex items-center mb-4">
              <User className="mr-2 text-gray-300" size={20} />
              <h2 className="text-lg">Affiliate Partner Details</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block mb-1 text-sm">
                  Name<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter Affiliate Partner Name"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm">
                  Phone Number<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="phoneNumber"
                  placeholder="Enter Phone Number"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm">
                  Email ID<span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="emailId"
                  placeholder="Enter Email ID"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.emailId}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm">
                  Password<span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter Password"
                    className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white pr-10"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div>
                <label className="block mb-1 text-sm">Location</label>
                <input
                  type="text"
                  name="location"
                  placeholder="Enter City, State"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.location}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm">Pin Code</label>
                <input
                  type="text"
                  name="pinCode"
                  placeholder="Enter Pin Code"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.pinCode}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm">Age</label>
                <input
                  type="number"
                  name="age"
                  placeholder="Enter Age"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.age}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div>
              <label className="block mb-1 text-sm">Affiliate Category</label>
              <div className="relative">
                <select
                  name="affiliateCategory"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-[#AAAAAA] appearance-none"
                  value={formData.affiliateCategory}
                  onChange={handleChange}
                >
                  <option value="">Select Affiliate Category</option>
                  <option value="governmentEmployee">
                    Government Employees
                  </option>
                  <option value="privateEmployee">Private Employees</option>
                  <option value="selfEmployed">Self Employed</option>
                  <option value="business">Business</option>
                  <option value="professional">Professional</option>
                  <option value="organisation">Organisations</option>
                  <option value="dailyWorker">Daily Workers</option>
                </select>

                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <ChevronRight className="transform rotate-90 h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>
            <div>
              <label className="block mb-1 text-sm mt-2">
                Date of Joining<span className="text-red-500">*</span>
              </label>

              <div className="relative">
                {/* Icon + Trigger */}
                <button
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      showCalendar: !formData.showCalendar,
                    })
                  }
                  className=" flex items-center justify-between p-2 bg-[#131313] border border-[#333333] rounded text-white"
                >
                  <span>
                    {formData.dateOfJoining
                      ? new Date(formData.dateOfJoining).toLocaleDateString(
                          "en-GB"
                        )
                      : "Select Date"}
                  </span>
                  <BsCalendarDate color="#FFCA20" className="ml-2" />
                </button>

                {/* Calendar Popup */}
                {formData.showCalendar && (
                  <div className="absolute z-50 mt-2 bg-[#1e1e1e] border border-[#333333] rounded-lg p-2">
                    <Calendar
                      onChange={(date) =>
                        setFormData({
                          ...formData,
                          dateOfJoining: date.toISOString(),
                          showCalendar: false,
                        })
                      }
                      value={
                        formData.dateOfJoining
                          ? new Date(formData.dateOfJoining)
                          : new Date()
                      }
                      calendarType="gregory"
                      prev2Label={null}
                      next2Label={null}
                      className="!bg-[#1e1e1e] !text-white !rounded-lg"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Social Profiles */}
          <div className="bg-[#131313] rounded-lg p-4 sm:p-6 mb-6">
            <div className="flex items-center mb-4">
              <svg
                className="mr-2 text-[#FFCA20]"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z" />
                <path d="M7 7h.01" />
              </svg>
              <h2 className="text-lg">Social Profiles</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block mb-1 text-sm">LinkedIn</label>
                <input
                  type="text"
                  name="linkedIn"
                  placeholder="LinkedIn Profile URL"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.linkedIn}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm">Instagram</label>
                <input
                  type="text"
                  name="instagram"
                  placeholder="Instagram Profile URL"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.instagram}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm">Facebook</label>
                <input
                  type="text"
                  name="facebook"
                  placeholder="Facebook Profile URL"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.facebook}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm">WhatsApp</label>
                <input
                  type="text"
                  name="whatsappNumber"
                  placeholder="WhatsApp Number (e.g. 919876543210)"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.whatsappNumber}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
          {/* Branch Details */}
          <div className="bg-[#131313] rounded-lg p-4 md:p-6 mb-6 w-full">
            <div className="flex flex-row gap-3">
              <User className="text-[#FFCA20] w-6 h-6" />
              <h2 className="text-lg font-medium mb-4">Branch Details</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block mb-2">
                  Branch Name*
                  <input
                    type="text"
                    placeholder="Enter Branch Name"
                    className="w-full bg-[#131313] border border-[#333333] rounded p-2 mt-1"
                    value={formData.branchName}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        branchName: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>
              <div>
                <label className="block mb-2">
                  Branch Type*
                  <select
                    className="w-full bg-[#131313] border border-[#333333] rounded p-2 mt-1"
                    value={formData.branchType}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        branchType: e.target.value,
                      }))
                    }
                  >
                    <option value="">Select Branch Type</option>
                    <option value="district">District level</option>
                    <option value="mandal">Mandal level</option>
                    <option value="village">Village level</option>
                  </select>
                </label>
              </div>
              <div>
                <label className="block mb-2">
                  Branch Location*
                  <input
                    type="text"
                    placeholder="Enter Branch Location"
                    className="w-full bg-[#131313] border border-[#333333] rounded p-2 mt-1"
                    value={formData.branchLocation}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        branchLocation: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>
            </div>
          </div>
          {/* Affiliate Bank Account Details */}
          <div className="bg-[#131313] rounded-lg p-4 sm:p-6 mb-6">
            <div className="flex items-center mb-4">
              <Landmark className="mr-2 text-[#FFCA20]" size={20} />
              <h2 className="text-lg">Affiliate Bank Account Details</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block mb-1 text-sm">
                  Account Holder Name<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="accountHolderName"
                  placeholder="Enter Account Holder Name"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.accountHolderName}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm">
                  Account Number<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="accountNumber"
                  placeholder="Enter Account Number"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.accountNumber}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm">
                  Bank Name<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="bankName"
                  placeholder="Enter Bank Name"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.bankName}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm">
                  Bank Branch<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="bankBranch"
                  placeholder="Enter Bank Branch Name"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.bankBranch}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm">
                  IFSC Code<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="ifscCode"
                  placeholder="Enter your IFSC Code"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.ifscCode}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Commission Structure */}
          <div className="bg-[#131313] rounded-lg p-4 sm:p-6 mb-6">
            <div className="flex items-center mb-4">
              <svg
                className="mr-2 text-[#FFCA20]"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z" />
                <path d="M7 7h.01" />
              </svg>
              <h2 className="text-lg">Commission Structure</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block mb-1 text-sm">
                  Commission Rate (%)<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="commissionRate"
                  placeholder="Enter Commission Rate"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.commissionRate}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm">
                  Fixed Commission<span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">₹</span>
                  <input
                    type="text"
                    name="fixedCommission"
                    placeholder="Enter Amount"
                    className="w-full p-2 pl-6 bg-[#131313] border border-[#333333] rounded text-white"
                    value={formData.fixedCommission}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label className="block mb-1 text-sm">Payout Cycle</label>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 space-x-0 sm:space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payoutCycle"
                      value="weekly"
                      checked={formData.payoutCycle === "weekly"}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span>Weekly</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payoutCycle"
                      value="monthly"
                      checked={formData.payoutCycle === "monthly"}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span>Monthly</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payoutCycle"
                      value="quarterly"
                      checked={formData.payoutCycle === "quarterly"}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span>Quarterly</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block mb-1 text-sm">
                  Payment Threshold<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="paymentThreshold"
                  placeholder="Enter Maximum Payment Amount"
                  className="w-full p-2 bg-[#131313] border border-[#333333] rounded text-white"
                  value={formData.paymentThreshold}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Compliance & Verification */}
          <div className="bg-[#131313] rounded-lg p-4 sm:p-6 mb-6">
            <div className="flex items-center mb-4">
              <FileText className="mr-2 text-[#FFCA20]" size={20} />
              <h2 className="text-lg">Compliance & Verification</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* ID Proof */}
              <div>
                <label className="block mb-1 text-sm pb-2">
                  ID Proof<span className="text-red-500">*</span>
                </label>
                <div
                  className="border border-[#333333] rounded bg-[#131313] p-8 flex flex-col items-center justify-center cursor-pointer hover:border-yellow-500 transition-colors"
                  onClick={() =>
                    document.getElementById("idProofInput").click()
                  }
                >
                  <UploadCloud className="text-[#FFCA20] mb-2" size={24} />
                  <p className="mb-1">Click to browse files</p>
                  <p className="text-xs text-gray-500">
                    Supported formats: PDF, JPG, PNG (Max 2 MB)
                  </p>
                  {files.idProof && (
                    <p className="text-sm text-[#FFCA20] mt-2">
                      {files.idProof.name}
                    </p>
                  )}
                  <input
                    id="idProofInput"
                    type="file"
                    accept=".pdf,.jpg,.png"
                    onChange={(e) => handleFileChange(e, "idProof")}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Address Proof */}
              <div>
                <label className="block mb-1 text-sm pb-2">
                  Address Proof<span className="text-red-500">*</span>
                </label>
                <div
                  className="border border-[#333333] rounded bg-[#131313] p-8 flex flex-col items-center justify-center cursor-pointer hover:border-yellow-500 transition-colors"
                  onClick={() =>
                    document.getElementById("addressProofInput").click()
                  }
                >
                  <UploadCloud className="text-[#FFCA20] mb-2" size={24} />
                  <p className="mb-1">Click to browse files</p>
                  <p className="text-xs text-gray-500">
                    Supported formats: PDF, JPG, PNG (Max 2 MB)
                  </p>
                  {files.addressProof && (
                    <p className="text-sm text-[#FFCA20] mt-2">
                      {files.addressProof.name}
                    </p>
                  )}
                  <input
                    id="addressProofInput"
                    type="file"
                    accept=".pdf,.jpg,.png"
                    onChange={(e) => handleFileChange(e, "addressProof")}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Bank Statement */}
              <div>
                <label className="block mb-1 text-sm pb-2">
                  Bank Statement (Last 6 months)
                  <span className="text-red-500">*</span>
                </label>
                <div
                  className="border border-[#333333] rounded bg-[#131313] p-8 flex flex-col items-center justify-center cursor-pointer hover:border-yellow-500 transition-colors"
                  onClick={() =>
                    document.getElementById("bankStatementInput").click()
                  }
                >
                  <UploadCloud className="text-[#FFCA20] mb-2" size={24} />
                  <p className="mb-1">Click to browse files</p>
                  <p className="text-xs text-gray-500">
                    Supported formats: PDF, JPG, PNG (Max 2 MB)
                  </p>
                  {files.bankStatement && (
                    <p className="text-sm text-[#FFCA20] mt-2">
                      {files.bankStatement.name}
                    </p>
                  )}
                  <input
                    id="bankStatementInput"
                    type="file"
                    accept=".pdf,.jpg,.png"
                    onChange={(e) => handleFileChange(e, "bankStatement")}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Agreement */}
              <div>
                <label className="block mb-1 text-sm pb-2">
                  Agreement<span className="text-red-500">*</span>
                </label>
                <div
                  className="border border-[#333333] rounded bg-[#131313] p-8 flex flex-col items-center justify-center cursor-pointer hover:border-yellow-500 transition-colors"
                  onClick={() =>
                    document.getElementById("agreementInput").click()
                  }
                >
                  <UploadCloud className="text-[#FFCA20] mb-2" size={24} />
                  <p className="mb-1">Click to browse files</p>
                  <p className="text-xs text-gray-500">
                    Supported formats: PDF, JPG, PNG (Max 2 MB)
                  </p>
                  {files.agreement && (
                    <p className="text-sm text-[#FFCA20] mt-2">
                      {files.agreement.name}
                    </p>
                  )}
                  <input
                    id="agreementInput"
                    type="file"
                    accept=".pdf,.jpg,.png"
                    onChange={(e) => handleFileChange(e, "agreement")}
                    className="hidden"
                  />
                </div>
              </div>

              {/* ✅ Canceled Cheque */}
              <div>
                <label className="block mb-1 text-sm pb-2">
                  Canceled Cheque<span className="text-red-500">*</span>
                </label>
                <div
                  className="border border-[#333333] rounded bg-[#131313] p-8 flex flex-col items-center justify-center cursor-pointer hover:border-yellow-500 transition-colors"
                  onClick={() =>
                    document.getElementById("canceledCheckInput").click()
                  }
                >
                  <UploadCloud className="text-[#FFCA20] mb-2" size={24} />
                  <p className="mb-1">Click to browse files</p>
                  <p className="text-xs text-gray-500">
                    Supported formats: PDF, JPG, PNG (Max 2 MB)
                  </p>
                  {files.canceledCheck && (
                    <p className="text-sm text-[#FFCA20] mt-2">
                      {files.canceledCheck.name}
                    </p>
                  )}
                  <input
                    id="canceledCheckInput"
                    type="file"
                    accept=".pdf,.jpg,.png"
                    onChange={(e) => handleFileChange(e, "canceledCheck")}
                    className="hidden"
                  />
                </div>
              </div>

              {/* ✅ Profile Photo */}
              <div>
                <label className="block mb-1 text-sm pb-2">
                  Profile Photo<span className="text-red-500">*</span>
                </label>
                <div
                  className="border border-[#333333] rounded bg-[#131313] p-8 flex flex-col items-center justify-center cursor-pointer hover:border-yellow-500 transition-colors"
                  onClick={() =>
                    document.getElementById("profilePhotoInput").click()
                  }
                >
                  <UploadCloud className="text-[#FFCA20] mb-2" size={24} />
                  <p className="mb-1">Click to browse files</p>
                  <p className="text-xs text-gray-500">
                    Supported formats: JPG, PNG (Max 2 MB)
                  </p>
                  {files.profilePhoto && (
                    <p className="text-sm text-[#FFCA20] mt-2">
                      {files.profilePhoto.name}
                    </p>
                  )}
                  <input
                    id="profilePhotoInput"
                    type="file"
                    accept=".jpg,.png"
                    onChange={(e) => handleFileChange(e, "profilePhoto")}
                    className="hidden"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-[#131313] rounded-lg p-4 sm:p-6 mb-6 flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 bg-[#131313] border border-[#2D2D2D] rounded order-2 sm:order-1"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-[#FFCA20] text-black font-medium rounded order-1 sm:order-2 flex items-center justify-center"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-black"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating...
                </>
              ) : (
                "Create Affiliate Partner"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
